import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {faArrowLeft, faArrowRight} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "../MainPage.styled";
import ActionButton from "../../../lib/UIBookBrd/ActionButton";
import useS115 from "../../../lib/Hooks/useS115";
import {RENT_LIST_PAGE} from "../../../Routes";
import {getProductIcon, getProductName, hasNumericPrice, ProductEntry} from "../../../lib/products";

type GearRailProps = {
    products: ProductEntry[]
}

const MAX_ITEMS = 10

const SCROLL_STEP = 320

/**
 * Horizontal showcase of the gear currently in rotation — the closest thing a
 * rental company has to a portfolio of work.
 */
const GearRail = ({products}: GearRailProps) => {

    const {t} = useTranslation()

    const {navigatePage} = useS115()

    const railRef = useRef<HTMLDivElement | null>(null)

    const featured = [...products]
        .sort((first, second) => {
            const flagged = Number(!!second.value?.isSpecialPrice) - Number(!!first.value?.isSpecialPrice)
            if (flagged !== 0) {
                return flagged
            }
            const firstPrice = hasNumericPrice(first.value?.rentPrice) ? first.value.rentPrice : 0
            const secondPrice = hasNumericPrice(second.value?.rentPrice) ? second.value.rentPrice : 0
            return secondPrice - firstPrice
        })
        .slice(0, MAX_ITEMS)

    if (!featured.length) {
        return null
    }

    const scrollBy = (direction: number) => {
        railRef.current?.scrollBy({left: direction * SCROLL_STEP, behavior: 'smooth'})
    }

    return (
        <Styled.Section>
            <Styled.SectionHead>
                <Styled.SectionHeadText>
                    <Styled.Eyebrow>{t('lbl_featured_gear')}</Styled.Eyebrow>
                    <Styled.SectionTitle>{t('lbl_featured_gear_title')}</Styled.SectionTitle>
                    <Styled.SectionLead>{t('lbl_featured_gear_lead')}</Styled.SectionLead>
                </Styled.SectionHeadText>
                <Styled.SectionSideAction>
                    <Styled.RailControls>
                        <Styled.RailButton
                            onClick={() => scrollBy(-1)}
                            aria-label={t('lbl_previous')}
                        >
                            <Styled.RailButtonIcon icon={faArrowLeft}/>
                        </Styled.RailButton>
                        <Styled.RailButton
                            onClick={() => scrollBy(1)}
                            aria-label={t('lbl_next')}
                        >
                            <Styled.RailButtonIcon icon={faArrowRight}/>
                        </Styled.RailButton>
                    </Styled.RailControls>
                </Styled.SectionSideAction>
            </Styled.SectionHead>

            <Styled.RailViewport>
                <Styled.Rail ref={railRef}>
                    {featured.map(({id, value}) => (
                        <Styled.RailCard
                            key={id}
                            onClick={() => navigatePage(RENT_LIST_PAGE)}
                        >
                            <Styled.RailMedia>
                                <Styled.RailImage
                                    src={value?.image}
                                    alt={id}
                                    fit={'contain'}
                                    icon={getProductIcon(value?.type)}
                                />
                            </Styled.RailMedia>
                            <Styled.RailBody>
                                <Styled.RailProducer>
                                    {value?.producent ?? value?.type ?? 'S115'}
                                </Styled.RailProducer>
                                <Styled.RailName>
                                    {getProductName(id, value, t)}
                                </Styled.RailName>
                                <Styled.RailPrice>
                                    {hasNumericPrice(value?.rentPrice)
                                        ? `${value.rentPrice} PLN ${t('lbl_rent_day')}`
                                        : t('lbl_coming_soon')}
                                </Styled.RailPrice>
                            </Styled.RailBody>
                        </Styled.RailCard>
                    ))}
                </Styled.Rail>
            </Styled.RailViewport>

            <Styled.RailFooter>
                <ActionButton
                    variant={'outline'}
                    label={t('lbl_see_all_offer')}
                    iconRight={faArrowRight}
                    onClick={() => navigatePage(RENT_LIST_PAGE)}
                />
            </Styled.RailFooter>
        </Styled.Section>
    )
}

export default GearRail
